import { createRootRoute, createRoute, createRouter, Outlet } from '@tanstack/react-router';
import { ActivitiesPage, ActivityDetailPage } from '../features/activities/ActivitiesPages';
import { ForgotPasswordPage, LoginPage, RegisterPage, ResetPasswordPage, VerifyEmailPage } from '../features/auth/AuthPages';
import { ChildPage, ChildrenPage } from '../features/children/ChildrenPages';
import { DashboardPage } from '../features/dashboard/DashboardPage';
import { DecisionsPage, DelegationPage, PriorityPage } from '../features/decisions/DecisionPages';
import { DownloadPage } from '../features/download/DownloadPage';
import { NotificationsPage } from '../features/notifications/NotificationsPage';
import { AcceptInvitePage, CreateFamilyPage } from '../features/onboarding/OnboardingPages';
import { AccountPage } from '../features/settings/AccountPage';
import { FamilySettingsPage } from '../features/settings/FamilySettingsPage';
import { SeriesDetailPage, SeriesListPage } from '../features/tasks/SeriesPages';
import { TaskDetailPage } from '../features/tasks/TaskDetailPage';
import { MineTasksPage, PartnerTasksPage, PoolPage } from '../features/tasks/TaskListPages';
import { AppLayout } from './AppShell';

// Routes are declared one by one (not via helpers) so TanStack Router can type every path and param.
const root = createRootRoute({ component: () => <Outlet /> });

const login = createRoute({
  getParentRoute: () => root,
  path: '/login',
  validateSearch: (s: Record<string, unknown>): { redirect?: string; error?: string } => ({
    ...(typeof s['redirect'] === 'string' ? { redirect: s['redirect'] } : {}),
    ...(typeof s['error'] === 'string' ? { error: s['error'].slice(0, 40) } : {}),
  }),
  component: LoginPage,
});
const register = createRoute({ getParentRoute: () => root, path: '/register', component: RegisterPage });
const verifyEmail = createRoute({ getParentRoute: () => root, path: '/verify-email', component: VerifyEmailPage });
const forgotPassword = createRoute({ getParentRoute: () => root, path: '/forgot-password', component: ForgotPasswordPage });
const resetPassword = createRoute({ getParentRoute: () => root, path: '/reset-password', component: ResetPasswordPage });
const invite = createRoute({ getParentRoute: () => root, path: '/invite', component: AcceptInvitePage });
const onboarding = createRoute({ getParentRoute: () => root, path: '/onboarding', component: CreateFamilyPage });
const download = createRoute({
  getParentRoute: () => root,
  path: '/download',
  component: DownloadPage,
  validateSearch: (s: Record<string, unknown>): { from?: 'app' } => (s['from'] === 'app' ? { from: 'app' } : {}),
});

const app = createRoute({ getParentRoute: () => root, id: 'app', component: AppLayout });
const dashboard = createRoute({ getParentRoute: () => app, path: '/', component: DashboardPage });
const pool = createRoute({ getParentRoute: () => app, path: '/pool', component: PoolPage });
const mine = createRoute({ getParentRoute: () => app, path: '/mine', component: MineTasksPage });
const partner = createRoute({ getParentRoute: () => app, path: '/partner', component: PartnerTasksPage });
const children = createRoute({ getParentRoute: () => app, path: '/children', component: ChildrenPage });
const child = createRoute({ getParentRoute: () => app, path: '/children/$childId', component: ChildPage });
const task = createRoute({ getParentRoute: () => app, path: '/tasks/$taskId', component: TaskDetailPage });
const recurring = createRoute({ getParentRoute: () => app, path: '/recurring', component: SeriesListPage });
const series = createRoute({ getParentRoute: () => app, path: '/recurring/$seriesId', component: SeriesDetailPage });
const activities = createRoute({ getParentRoute: () => app, path: '/activities', component: ActivitiesPage });
const activity = createRoute({ getParentRoute: () => app, path: '/activities/$activityId', component: ActivityDetailPage });
const decisions = createRoute({ getParentRoute: () => app, path: '/decisions', component: DecisionsPage });
const delegation = createRoute({ getParentRoute: () => app, path: '/decisions/delegations/$proposalId', component: DelegationPage });
const priority = createRoute({ getParentRoute: () => app, path: '/decisions/priority/$requestId', component: PriorityPage });
const notifications = createRoute({ getParentRoute: () => app, path: '/notifications', component: NotificationsPage });
const settings = createRoute({ getParentRoute: () => app, path: '/settings', component: FamilySettingsPage });
const account = createRoute({ getParentRoute: () => app, path: '/account', component: AccountPage });

const routeTree = root.addChildren([
  login,
  register,
  verifyEmail,
  forgotPassword,
  resetPassword,
  invite,
  onboarding,
  download,
  app.addChildren([dashboard, pool, mine, partner, children, child, task, recurring, series, decisions, delegation, priority, notifications, settings, account, activities, activity]),
]);

export const router = createRouter({ routeTree, defaultPreload: 'intent', scrollRestoration: true });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
